import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme, type SxProps, type Theme } from "@mui/material/styles";
import { OrionResourceCardBox } from "@orion/OrionComponents/OrionResourceCardBox";
import { useState, type MouseEvent, type ReactNode } from "react";

import { primaryActionLinkProps } from "./primaryActionLinkProps";
import {
  ResourceCardFooterFlexSpacer,
  ResourceCardImageCover,
  ResourceCardImageRegionRoot,
  ResourceCardImageTileFooterStack,
} from "./SkuStoreResourceCard.styles";
import type {
  SKUStoreResourceCardFooterActionsRowProps,
  SKUStoreResourceCardImageRegionProps,
  SKUStoreResourceCardPrimaryAction,
  SKUStoreResourceCardSlotProps,
} from "@/features/SKUStore/components/ResourceCard/SKUStoreResourceCard.types";
import type { SKUStoreStatusBadgeItem } from "@/features/SKUStore/components/Status/statusBadgeItems";

import { SKUStoreStatusBadgeRow } from "@/features/SKUStore/components/Status/SKUStoreStatusBadgeRow";

/** Footer controls (status chips, Edit) sit this far inside the card border. */
const TILE_CONTROL_INSET_PX = 8;

/** Footer controls share the status chips' height. */
const TILE_CONTROL_HEIGHT_PX = 28;

/**
 * Radius for footer controls, concentric with the card corner: the card's
 * radius less its 1px border and the controls' inset.
 */
function tileControlRadius(theme: Theme): number {
  return theme.shape.borderRadiusLg - 1 - TILE_CONTROL_INSET_PX;
}

/** Shared with product header — neutral placeholder when `src` is missing or fails to load. */
export const PRODUCT_PLACEHOLDER_IMAGE =
  "https://cdn.queen.one/connect/ui/product-placeholder.png";

export function CardImageRegion({
  imageSrc,
  imageAlt = "Product image",
  aspectRatio = "1 / 1",
  sx,
}: SKUStoreResourceCardImageRegionProps) {
  const [failed, setFailed] = useState(false);
  const resolvedSrc: string =
    failed || imageSrc == null || imageSrc === ""
      ? PRODUCT_PLACEHOLDER_IMAGE
      : imageSrc;

  return (
    <ResourceCardImageRegionRoot regionAspectRatio={aspectRatio} sx={sx}>
      <ResourceCardImageCover
        alt={imageAlt}
        decoding="async"
        loading="lazy"
        onError={() => setFailed(true)}
        src={resolvedSrc}
      />
    </ResourceCardImageRegionRoot>
  );
}

export function StatusPill({ children }: SKUStoreResourceCardSlotProps) {
  return (
    <Box
      sx={{
        alignSelf: "flex-start",
        bgcolor: "success.bgContainer",
        borderRadius: (theme) => theme.shape.borderRadiusLg,
        px: 3,
        py: 2,
      }}
    >
      <Typography
        variant="body3"
        sx={{
          color: "success.dark",
          fontWeight: (t) => t.typography.fontWeightScale.semiBold,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

/** Leading status + trailing actions (or metadata) — shared by media/variant tiles and review cards. */
export function FooterActionsRow({
  statusSlot,
  actionsSlot,
  sx,
}: SKUStoreResourceCardFooterActionsRowProps) {
  if (statusSlot == null && actionsSlot == null) {
    return null;
  }
  const baseSx: SxProps<Theme> = {
    pb: 2,
    flexWrap: "wrap",
    rowGap: 2,
    width: 1,
    alignItems: "center",
  };
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={sx == null ? baseSx : ([baseSx, sx] as SxProps<Theme>)}
    >
      {statusSlot}
      <ResourceCardFooterFlexSpacer />
      {actionsSlot}
    </Stack>
  );
}

/**
 * Status badges plus a trailing action, on one line. Badges that do not fit
 * fold into a "+N" chip (see {@link SKUStoreStatusBadgeRow}), so neither the
 * badges nor the action ever push the card taller.
 */
export function BadgeActionsRow({
  badges,
  action,
  onBadgesClick,
  badgesSx,
  actionSx,
}: {
  badges: SKUStoreStatusBadgeItem[];
  action?: ReactNode;
  onBadgesClick?: (event: MouseEvent<HTMLDivElement>) => void;
  badgesSx?: SxProps<Theme>;
  actionSx?: SxProps<Theme>;
}) {
  const theme = useTheme();
  const actionBaseSx: SxProps<Theme> = { flexShrink: 0 };
  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{ width: 1, alignItems: "center", flexWrap: "nowrap", minWidth: 0 }}
    >
      <SKUStoreStatusBadgeRow
        items={badges}
        onClick={onBadgesClick}
        sx={badgesSx}
        badgeRadius={tileControlRadius(theme)}
      />
      {action != null ? (
        <Box
          sx={
            actionSx == null
              ? actionBaseSx
              : ([actionBaseSx, actionSx] as SxProps<Theme>)
          }
        >
          {action}
        </Box>
      ) : null}
    </Stack>
  );
}

export function TilePrimaryActionButton({
  action,
}: {
  action: SKUStoreResourceCardPrimaryAction;
}) {
  return (
    <Button
      {...primaryActionLinkProps(action)}
      color="inherit"
      disabled={action.onClick == null && action.link == null}
      onClick={action.onClick}
      size="small"
      variant="outlined"
      sx={(theme) => ({
        borderRadius: `${String(tileControlRadius(theme))}px`,
        // Chip height, so both bottom corners sit the same inset from the
        // card's edge and stay concentric with it.
        height: TILE_CONTROL_HEIGHT_PX,
      })}
    >
      {action.label}
    </Button>
  );
}

/** Shared chrome, hero image, and footer stack for media + variant layouts. */
export function ImageTileShell({
  topSlot,
  imageAlt,
  imageSrc,
  titleBlock,
  statusSlot,
  actionsSlot,
}: {
  topSlot?: ReactNode;
  imageAlt: string;
  imageSrc?: string | null;
  titleBlock: ReactNode;
  statusSlot?: ReactNode;
  actionsSlot?: ReactNode;
}) {
  return (
    <OrionResourceCardBox>
      {topSlot}
      <CardImageRegion imageAlt={imageAlt} imageSrc={imageSrc} />
      {/* The footer pads to the controls' inset, so the chips and Edit tuck
          into the corners; the text gets the rest of its side padding back. */}
      <ResourceCardImageTileFooterStack
        spacing={3}
        sx={{ px: `${String(TILE_CONTROL_INSET_PX)}px` }}
      >
        <Box
          sx={(theme) => ({
            px: `calc(${theme.spacing(5)} - ${String(TILE_CONTROL_INSET_PX)}px)`,
          })}
        >
          {titleBlock}
        </Box>
        <FooterActionsRow
          actionsSlot={actionsSlot}
          statusSlot={statusSlot}
          // No bottom padding or row gap: the chip row fills the width, so
          // the spacer wraps onto an empty second line that the gap would
          // otherwise add below the controls.
          sx={{ pb: 0, rowGap: 0 }}
        />
      </ResourceCardImageTileFooterStack>
    </OrionResourceCardBox>
  );
}
